/**
 * Job Hunter — the AI review of one CV: pick the model, run it, then the score,
 * the per-area bars, the free checks, the skills the matched jobs ask for, the
 * issues with fixes, rewrite suggestions and country conventions.
 */
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Check, CircleAlert, Copy, Globe2, ListChecks, LoaderCircle, Sparkles, Stethoscope, Tags, ThumbsUp, TrendingUp, Wand2, X } from 'lucide-react';
import { Button } from '@/components/Button';
import { ModelPicker, type ModelPickerValue } from '@/components/ModelPicker';
import type { AiProviderName } from '@/services/settings.service';
import { jobHunterService, type JhCv, type JhCvHealth, type JhMarketDemand, type JhProfile } from '@/services/job-hunter.service';
import { CV_CATEGORY_LABEL, copyText, fmtDateTime, scoreTone } from '@/lib/job-hunter/format';
import { Pill, ScoreRing } from './ui';

const MODEL_KEY = 'jh.cvReviewModel';
const readModel = (): ModelPickerValue | undefined => { try { const v = localStorage.getItem(MODEL_KEY); return v ? JSON.parse(v) as ModelPickerValue : undefined; } catch { return undefined; } };
const writeModel = (v: ModelPickerValue) => { try { localStorage.setItem(MODEL_KEY, JSON.stringify(v)); } catch { /* storage blocked */ } };

const isFull = (h: JhCv['health']): h is JhCvHealth => !!h && Array.isArray((h as JhCvHealth).checks);
const SEVERITY = { high: { label: 'High', tone: 'danger' as const, rank: 0 }, medium: { label: 'Medium', tone: 'warning' as const, rank: 1 }, low: { label: 'Low', tone: 'info' as const, rank: 2 } };

function Block({ icon, title, sub, children }: { icon: React.ReactNode; title: string; sub?: string; children: React.ReactNode }) {
  return (
    <section className="jh-card jh-card-pad">
      <div className="jh-card-title">{icon}{title}</div>
      {sub && <div className="jh-card-sub">{sub}</div>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

export function DemandChips({ demand, limit = 24 }: { demand: JhMarketDemand[]; limit?: number }) {
  if (!demand.length) return <p className="jh-muted">Shows up once your agents have matched a few jobs — it compares their skills with this CV.</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {demand.slice(0, limit).map((d) => (
        <span key={d.term} className={clsx('jh-pill', d.inCv ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning')} title={d.inCv ? 'In this CV' : 'Asked for by matched jobs — not in this CV'}>
          {d.inCv ? <Check size={11} /> : <CircleAlert size={11} />}{d.term}<span className="opacity-70">· {d.jobs}</span>
        </span>
      ))}
    </div>
  );
}

export function CvHealthPanel({ cv, profile, autoRun, onAnalyzed }: { cv: JhCv; profile: JhProfile | null; autoRun?: boolean; onAnalyzed: (h: JhCvHealth) => void }) {
  const [model, setModel] = useState<ModelPickerValue | undefined>(() => readModel() ?? (profile?.aiProvider ? { provider: profile.aiProvider as AiProviderName, model: profile.aiModel ?? '' } : undefined));
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const started = useRef(false);
  const health = isFull(cv.health) ? cv.health : null;
  const demand = health?.demand ?? cv.demand ?? [];
  const readable = (cv.text ?? '').trim().length >= 200;

  useEffect(() => {
    if (!busy) return;
    setElapsed(0);
    const t = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => window.clearInterval(t);
  }, [busy]);

  const analyze = async () => {
    if (!readable) { toast.error('This CV has no readable text — paste it in the Text tab first'); return; }
    setBusy(true);
    try {
      const h = await jobHunterService.analyzeCv(cv.cvUid, model ? { provider: model.provider, model: model.model } : {});
      onAnalyzed(h);
      toast.success(`CV reviewed — health ${h.score}`);
    } catch { /* toasted */ } finally { setBusy(false); }
  };
  useEffect(() => { if (autoRun && !started.current && readable) { started.current = true; void analyze(); } }); // eslint-disable-line react-hooks/exhaustive-deps

  const issues = [...(health?.issues ?? [])].sort((a, b) => SEVERITY[a.severity].rank - SEVERITY[b.severity].rank);
  const failed = (health?.checks ?? []).filter((c) => !c.ok).length;

  return (
    <div className="space-y-4">
      <section className="jh-card jh-card-pad">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="jh-card-title"><Stethoscope size={16} className="text-primary" />CV health</div>
          {health && <span className="text-[11.5px] text-text-muted">Reviewed {fmtDateTime(health.at)}{health.model ? ` · ${health.provider ?? ''} ${health.model}` : health.provider ? ` · ${health.provider}` : ''}</span>}
        </div>
        <div className="jh-card-sub">An AI recruiter reads the CV against your agents’ roles and countries and the jobs they matched. It never adds experience — rewrites keep your facts.</div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="min-w-0 flex-1"><ModelPicker value={model} onChange={(v) => { setModel(v); writeModel(v); }} size="sm" label="Model for the CV review" className="w-full" disabled={busy} /></div>
          <Button size="sm" loading={busy} disabled={!readable} iconLeft={<Sparkles size={14} />} onClick={analyze}>{health ? 'Review again' : 'Analyze with AI'}</Button>
        </div>
        {busy && (
          <div className="mt-3 flex items-center gap-2.5 rounded-xl bg-primary-soft px-3 py-2.5 text-[12.5px] text-primary">
            <LoaderCircle size={15} className="animate-spin" />
            {elapsed < 8 ? 'Running the automatic checks…' : elapsed < 25 ? 'The AI is reading your CV against the matched jobs…' : 'Writing issues, fixes and rewrites…'}
            <span className="ml-auto tabular-nums opacity-70">{elapsed}s</span>
          </div>
        )}
        {!readable && <p className="mt-3 rounded-xl bg-danger-soft px-3 py-2 text-[12.5px] text-danger">No readable text in this CV — ATS software can’t read it either. Paste the text in the Text tab (or upload a PDF exported from Word / Google Docs).</p>}

        {health && (
          <div className="mt-4 flex flex-wrap items-center gap-4 rounded-2xl border border-border p-4">
            <ScoreRing score={health.score} size={84} stroke={7} />
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-bold tracking-tight" style={{ color: scoreTone(health.score).color }}>{health.score >= 85 ? 'Strong CV' : health.score >= 70 ? 'Good, with gaps' : health.score >= 50 ? 'Needs work' : 'Weak'}</div>
              <p className="mt-0.5 text-[13px] leading-relaxed text-text-secondary">{health.verdict || 'Automatic checks only.'}</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                <Pill tone={failed ? 'warning' : 'success'}>{(health.checks.length - failed)}/{health.checks.length} checks passed</Pill>
                {issues.filter((i) => i.severity === 'high').length > 0 && <Pill tone="danger">{issues.filter((i) => i.severity === 'high').length} high-priority fix{issues.filter((i) => i.severity === 'high').length === 1 ? '' : 'es'}</Pill>}
              </div>
            </div>
          </div>
        )}
        {health && Object.keys(health.categories).length > 0 && (
          <div className="mt-4 space-y-2.5">
            {Object.entries(health.categories).map(([k, v]) => (
              <div key={k} className="grid grid-cols-[minmax(0,150px)_1fr_36px] items-center gap-3 text-[12.5px]">
                <span className="truncate text-text-secondary">{CV_CATEGORY_LABEL[k] ?? k}</span>
                <div className="jh-meter"><span style={{ width: `${v}%`, background: scoreTone(v).color }} /></div>
                <span className="text-right font-semibold tabular-nums">{v}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <Block icon={<TrendingUp size={15} className="text-primary" />} title="Market demand" sub="Skills the jobs your agents matched ask for — green ones are in this CV, amber ones are missing. Add only what you really have.">
        <DemandChips demand={demand} />
      </Block>

      {health && (
        <>
          <Block icon={<ListChecks size={15} className="text-primary" />} title="Automatic checks" sub="What an ATS or a 6-second recruiter skim notices first.">
            <ul className="space-y-1.5">
              {health.checks.map((c) => (
                <li key={c.id} className="flex items-start gap-2.5 text-[12.5px]">
                  <span className={clsx('mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full', c.ok ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger')}>{c.ok ? <Check size={12} /> : <X size={12} />}</span>
                  <span className="min-w-0"><b className="font-semibold">{c.label}</b>{c.detail && <span className="text-text-muted"> — {c.detail}</span>}</span>
                </li>
              ))}
            </ul>
          </Block>

          {issues.length > 0 && (
            <Block icon={<CircleAlert size={15} className="text-warning" />} title={`Issues & fixes (${issues.length})`}>
              <div className="space-y-2.5">
                {issues.map((i, n) => (
                  <div key={n} className="rounded-xl border border-border p-3">
                    <div className="flex flex-wrap items-center gap-1.5"><Pill tone={SEVERITY[i.severity].tone}>{SEVERITY[i.severity].label}</Pill><span className="jh-chip">{CV_CATEGORY_LABEL[i.area] ?? i.area}</span></div>
                    <p className="mt-2 text-[12.5px] text-text-secondary">{i.problem}</p>
                    <p className="mt-1.5 flex gap-1.5 text-[12.5px] font-medium"><Wand2 size={13} className="mt-0.5 shrink-0 text-primary" />{i.fix}</p>
                  </div>
                ))}
              </div>
            </Block>
          )}

          {health.rewrites.length > 0 && (
            <Block icon={<Wand2 size={15} className="text-primary" />} title="Rewrite suggestions" sub="Same facts, stronger lines. Fill any [placeholder] with your real number — or leave the line out.">
              <div className="space-y-3">
                {health.rewrites.map((r, n) => (
                  <div key={n} className="rounded-xl border border-border p-3 text-[12.5px]">
                    <div className="text-text-muted line-through decoration-danger/40">{r.before}</div>
                    <div className="mt-1.5 flex items-start gap-2">
                      <p className="min-w-0 flex-1 font-medium text-text-primary">{r.after}</p>
                      <button type="button" title="Copy" className="grid h-7 w-7 shrink-0 place-items-center rounded-lg border border-border text-text-muted hover:text-primary" onClick={async () => { const ok = await copyText(r.after); toast[ok ? 'success' : 'error'](ok ? 'Copied' : 'Copy failed'); }}><Copy size={13} /></button>
                    </div>
                    {r.why && <div className="mt-1 text-[11.5px] text-text-muted">{r.why}</div>}
                  </div>
                ))}
              </div>
            </Block>
          )}

          <div className="grid gap-4 xl:grid-cols-2">
            {health.strengths.length > 0 && (
              <Block icon={<ThumbsUp size={15} className="text-success" />} title="Strengths">
                <ul className="space-y-1.5 text-[12.5px]">{health.strengths.map((s) => <li key={s} className="flex gap-2"><Check size={13} className="mt-0.5 shrink-0 text-success" />{s}</li>)}</ul>
              </Block>
            )}
            {health.missingKeywords.length > 0 && (
              <Block icon={<Tags size={15} className="text-warning" />} title="Missing keywords" sub="Only add the ones you have really used.">
                <div className="flex flex-wrap gap-1.5">{health.missingKeywords.map((k) => <span key={k} className="jh-pill bg-warning-soft text-warning">{k}</span>)}</div>
              </Block>
            )}
          </div>

          {health.countryTips.length > 0 && (
            <Block icon={<Globe2 size={15} className="text-primary" />} title="Country conventions" sub={health.targetCountries.length ? `For ${health.targetCountries.join(', ')}` : undefined}>
              <ul className="space-y-1.5 text-[12.5px]">{health.countryTips.map((t) => <li key={t} className="flex gap-2"><Globe2 size={13} className="mt-0.5 shrink-0 text-text-muted" />{t}</li>)}</ul>
            </Block>
          )}
        </>
      )}
    </div>
  );
}
